import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import BAlert, { bAlertType } from '../../general/BAlert';
import Loading from '../../general/Loading';
import Login from '../../../records/customer/Login';
import ModalActions from '../../../actions/ModalActions';
import t from '../../../lib/I18n';

import { AmazonLoginStatus, GlobalModals } from '../../../constants/AppConstants';
import connectToStores from '../../../helpers/connectToStores';
import AmazonLinkModal from './AmazonLinkModal';
import AmazonLoginButton from './AmazonLoginButton';
import AmazonLoginActions from '../../../actions/AmazonLoginActions';
import AmazonLoginStore from '../../../stores/AmazonLoginStore';
import AmazonRegisterFirstModal from './AmazonRegisterFirstModal';
import FeatureFlagStore from '../../../stores/FeatureFlagStore';

const _getStateFromStores = () => {
    return {
        accessToken: AmazonLoginStore.getAccessToken(),
        amazonEmail: AmazonLoginStore.getAmazonEmail(),
        customer: AmazonLoginStore.getCustomer(),
        enableAmazonRegistration: FeatureFlagStore.get('enableAmazonRegistration'),
        hasLoginCompleted: AmazonLoginStore.getHasLoginCompleted(),
        isLoginProcessing: AmazonLoginStore.getIsProcessing(),
        status: AmazonLoginStore.getStatus(),
    };
};

class AmazonLogin extends PureComponent {
    static propTypes = {
        accessToken: PropTypes.string,
        amazonEmail: PropTypes.string,
        closeModal: PropTypes.func,
        code: PropTypes.string,
        customer: PropTypes.object,
        enableAmazonRegistration: PropTypes.bool,
        hasLoginCompleted: PropTypes.bool,
        hideErrors: PropTypes.bool,
        isAlreadyRegistering: PropTypes.bool,
        isLoginProcessing: PropTypes.bool,
        isLongButton: PropTypes.bool,
        onButtonClick: PropTypes.func,
        status: PropTypes.string
    }

    static defaultProps = {
        accessToken: null,
        amazonEmail: null,
        closeModal: _.noop,
        code: null,
        customer: null,
        enableAmazonRegistration: false,
        hasLoginCompleted: false,
        hideErrors: false,
        isAlreadyRegistering: false,
        isLoginProcessing: false,
        isLongButton: false,
        onButtonClick: _.noop,
        status: null
    }

    state = {
        disableContinue: true,
        loginForm: new Login(),
        isProcessing: false,
        showLinkModal: false,
        showRegisterFirstModal: false,
        submitted: false
    };

    componentWillMount() {
        AmazonLoginActions.load();
    }

    componentDidUpdate({
        status: prevStatus
    }) {
        const { accessToken, closeModal, customer, isAlreadyRegistering, status } = this.props;

        if (status && status !== prevStatus) {
            this.setState({ submitted: false });

            switch (status) {
                case AmazonLoginStatus.LINK_START:
                    _.defer(AmazonLoginActions.resetStatus);
                    this.openLinkAccountModal();
                    break;

                case AmazonLoginStatus.LINK_SUCCESS:
                    _.defer(() => {
                        AmazonLoginActions.resetStatus();
                        ModalActions.open(GlobalModals.INFO, {
                            type: 'success',
                            heading: t('login.link.amazon.success.heading'),
                            title: t('login.link.amazon.success.title', { accountType: t('app.owner.short') }),
                            message: t(`login.link.amazon.success.message`)
                        });
                    });
                    break;

                case AmazonLoginStatus.LOGIN_READY_TO_AUTH:
                    _.defer(() => {
                        AmazonLoginActions.resetStatus();
                        AmazonLoginActions.authenticate({ accessToken });
                    });
                    break;

                case AmazonLoginStatus.LOGIN_SUCCESS:
                    _.defer(AmazonLoginActions.resetStatus);
                    closeModal(status);
                    break;

                case AmazonLoginStatus.REGISTER_START:
                    _.defer(() => {
                        AmazonLoginActions.resetStatus();

                        if (isAlreadyRegistering) {
                            this.showRegistrationForm();
                        } else {
                            this.setState({ showRegisterFirstModal: true });
                        }
                    });
                    break;

                case AmazonLoginStatus.SYNC_EMAIL_START:
                    _.defer(() => {
                        AmazonLoginActions.resetStatus();
                        ModalActions.open(GlobalModals.SYNC_EMAIL_AMAZON_LOGIN, { customer });
                    });
                    break;
            }
        }
    }

    closeRegisterFirstModal = () =>
        this.setState({ showRegisterFirstModal: false });

    handleLoginClick = () => {
        this.props.onButtonClick();
        AmazonLoginActions.login();
    }

    handleCancelLink = () => {
        this.setState({ showLinkModal: false });
        AmazonLoginActions.resetStateToIdle();
    }

    handleFieldChange = (field, value) => {
        const { loginForm, submitted } = this.state;

        this.setState({
            disableContinue: !loginForm.isFieldValid('password', { 'password': value }) || submitted,
            loginForm: this.state.loginForm.set(field, value),
            submitted: false,
            status: null
        });
    };

    linkAccount = () => {
        const { customer } = this.props;
        const { loginForm, submitted } = this.state;

        if (loginForm.isFieldValid('password') && !submitted) {
            this.setState({ submitted: true });
            AmazonLoginActions.linkAccount({
                email: customer.email,
                password: loginForm.password
            });
        }
    }

    openLinkAccountModal = () => {
        this.setState({ showLinkModal: true });
    }

    showRegistrationForm = () => {
        const { accessToken, closeModal, customer, enableAmazonRegistration } = this.props;

        closeModal();

        // Wait with opening a new modal after the previous one closes
        _.defer(() => {
            ModalActions.open(GlobalModals.REGISTER, enableAmazonRegistration ? { amazonAccessToken: accessToken, customer } : null);
        });
    }

    render() {
        const { customer, hideErrors, isLoginProcessing, isLongButton, status } = this.props;
        const { isProcessing, loginForm, showLinkModal, showRegisterFirstModal, submitted } = this.state;

        return (
            <React.Fragment>
                { showLinkModal && <AmazonLinkModal
                    customer={ customer }
                    disableContinue={ this.disableContinue }
                    handleFieldChange={ this.handleFieldChange }
                    isProcessing={ isProcessing }
                    loginForm={ loginForm }
                    onClose={ this.handleCancelLink }
                    onContinue={ this.linkAccount }
                    status={ status }
                    submitted={ submitted }/>
                }
                { showRegisterFirstModal && <AmazonRegisterFirstModal
                    onContinue={ this.showRegistrationForm }
                    onClose={ this.closeRegisterFirstModal } /> }
                <div className="amazon-login">
                    { isLoginProcessing
                        ? <Loading
                            message={ t('global.loading') }
                            unwrapped
                            standalone
                        />
                        : <React.Fragment>
                                {status === AmazonLoginStatus.LOGIN_ERROR && !hideErrors && <BAlert type={ bAlertType.DANGER } className="margin-bottom--sm">
                                    { t('login.amazon.error') }
                                </BAlert>}
                                <AmazonLoginButton isLongButton={ isLongButton } onClick={ this.handleLoginClick } />
                            </React.Fragment> }
                </div>
            </React.Fragment>
        );
    }
}

export default connectToStores([AmazonLoginStore, FeatureFlagStore], _getStateFromStores)(AmazonLogin);



// WEBPACK FOOTER //
// ./src/components/login/amazon/AmazonLogin.js